import type { MessageId, ProviderKind, ServerProvider } from "@cmmd-forge/contracts";

import { useCanvasStore } from "../../canvasStateStore";
import type { ComposerFileAttachment, ComposerImageAttachment } from "../../composerDraftStore";
import {
  appendTerminalContextsToPrompt,
  type TerminalContextDraft,
} from "../../lib/terminalContext";
import {
  buildExpiredTerminalContextToastCopy,
  formatOutgoingPrompt,
  isDesignRelatedPrompt,
} from "../ChatView.logic";
import { toastManager } from "../ui/toast";
import type { ChatTurnSubmissionInput } from "./chatTurnSubmissionTypes";
import { IMAGE_ONLY_BOOTSTRAP_PROMPT } from "./chatTurnSubmissionPreflight";

const FILE_ONLY_BOOTSTRAP_PROMPT = "Attached file.";

export async function applyOptimisticChatTurn<ComposerTarget>(input: {
  readonly submission: ChatTurnSubmissionInput<ComposerTarget>;
  readonly prompt: string;
  readonly trimmedPrompt: string;
  readonly images: ReadonlyArray<ComposerImageAttachment>;
  readonly files: ReadonlyArray<ComposerFileAttachment>;
  readonly terminalContexts: ReadonlyArray<TerminalContextDraft>;
  readonly expiredTerminalContextCount: number;
  readonly selectedProvider: ProviderKind;
  readonly selectedModel: string;
  readonly selectedProviderModels: ReadonlyArray<ServerProvider["models"][number]>;
  readonly selectedPromptEffort: string | null;
  readonly messageId: MessageId;
  readonly messageCreatedAt: string;
}): Promise<string> {
  const { submission } = input;
  if (submission.canvasOpen && input.trimmedPrompt) {
    useCanvasStore.getState().requestCanvasGenerate(input.trimmedPrompt);
  } else if (
    !submission.canvasOpen &&
    input.trimmedPrompt &&
    isDesignRelatedPrompt(input.trimmedPrompt)
  ) {
    submission.onToggleCanvas(true);
    useCanvasStore.getState().requestCanvasGenerate(input.trimmedPrompt);
  }

  const messageText = appendTerminalContextsToPrompt(input.prompt, input.terminalContexts);
  const outgoingMessageText = formatOutgoingPrompt({
    provider: input.selectedProvider,
    model: input.selectedModel,
    models: input.selectedProviderModels,
    effort: input.selectedPromptEffort,
    text:
      messageText ||
      (input.images.length > 0 ? IMAGE_ONLY_BOOTSTRAP_PROMPT : FILE_ONLY_BOOTSTRAP_PROMPT),
  });
  const attachments = [
    ...input.images.map((image) => ({
      type: "image" as const,
      id: image.id,
      name: image.name,
      mimeType: image.mimeType,
      sizeBytes: image.sizeBytes,
      previewUrl: image.previewUrl,
    })),
    ...input.files.map((file) => ({
      type: "file" as const,
      id: file.id,
      name: file.name,
      mimeType: file.mimeType,
      sizeBytes: file.sizeBytes,
      downloadUrl: "",
    })),
  ];
  submission.isAtEndRef.current = true;
  submission.showScrollDebouncer.current.cancel();
  submission.setShowScrollToBottom(false);
  await submission.legendListRef.current?.scrollToEnd?.({ animated: false });
  submission.setOptimisticUserMessages((existing) => [
    ...existing,
    {
      id: input.messageId,
      role: "user",
      text: outgoingMessageText,
      ...(attachments.length > 0 ? { attachments } : {}),
      createdAt: input.messageCreatedAt,
      streaming: false,
    },
  ]);

  if (input.expiredTerminalContextCount > 0) {
    const copy = buildExpiredTerminalContextToastCopy(input.expiredTerminalContextCount, "omitted");
    toastManager.add({ type: "warning", title: copy.title, description: copy.description });
  }
  submission.promptRef.current = "";
  submission.clearComposerDraftContent(submission.composerDraftTarget);
  submission.composerRef.current?.resetCursorState();
  return outgoingMessageText;
}
